import {TransferMode, useGetAppSettingsQuery, useGetCompanyInfoQuery} from "../generated/graphql";
import {CurrencyShort} from "../helpers/renderAmount";

/**
 * Tenant-Standardwerte des Umbuchungs-Wizards (#662): `Setting.transferDefaultPriceOption`
 * und `Setting.transferDefaultOldBookingOption`. Als Strings durchgereicht — die Werte
 * heißen wie die GraphQL-Namen von `TransferPriceKind` bzw. der Optionsschlüssel des
 * Wizards; der Wizard fällt auf Kurspreis/„nach Regeln" zurück, wenn ein Wert nicht zu
 * seinen Optionen passt (z. B. alte Bundles oder künftige Optionen aus #663).
 */
export type TransferWizardDefaults = {
    priceOption?: string;
    oldBookingOption?: string;
};

export type UseTransferWizardModeResult = {
    /** true = der Umbuchen-Dialog arbeitet mit der Engine (#660, ADR 0006). */
    wizardMode: boolean;
    /** Solange true, ist `wizardMode` noch nicht entschieden. */
    loading: boolean;
    /** Fehler oder unvollständige Konfiguration: keine Buchungsaktion zulassen. */
    unavailable?: boolean;
    currency?: CurrencyShort;
    defaults?: TransferWizardDefaults;
};

/**
 * Effektiver Umbuchungs-Modus der Admin-App (#660, Teil 2c, E3).
 *
 * Zwei Bedingungen, beide muessen erfuellt sein:
 *   1. Superuser-Freigabe `featureTransferWizard` auf der Subscription — das ist der
 *      Vertrags-/Rollout-Schalter, den der Server auch fuer `executeTransfer` prueft.
 *   2. Tenant-Einstellung `transferMode === Wizard` — die Wahl des Kunden.
 *
 * Waehrend eine der beiden Abfragen laeuft, liefert der Hook bewusst `false`:
 * Aufrufer müssen währenddessen Buchungsaktionen sperren: `false` ist noch keine
 * Entscheidung für Legacy. Auch ein Ladefehler darf den Geldweg nicht umschalten.
 *
 * `cache-first` mit Absicht: beide Queries laufen in der App ohnehin, ein weiterer
 * Netz-Roundtrip pro Dialog-Oeffnung waere reine Latenz.
 */
export const useTransferWizardMode = (): UseTransferWizardModeResult => {
    const {data: companyInfoData, loading: companyInfoLoading, error: companyInfoError} = useGetCompanyInfoQuery({
        fetchPolicy: "cache-first",
    });
    const {data: settingsData, loading: settingsLoading, error: settingsError} = useGetAppSettingsQuery({
        fetchPolicy: "cache-first",
    });

    const loading = companyInfoLoading || settingsLoading;

    if (loading) {
        return {wizardMode: false, loading: true};
    }

    const featureEnabled = companyInfoData?.companyInfo?.subscription?.featureTransferWizard === true;
    const settingIsWizard = settingsData?.settings?.transferMode === TransferMode.Wizard;
    const currencyCode = settingsData?.settings?.currency?.toUpperCase();
    const currency = (["EUR", "CHF", "USD", "GBP"] as Array<CurrencyShort>).find((code) => code === currencyCode);
    const unavailable = Boolean(companyInfoError || settingsError || !companyInfoData?.companyInfo || !settingsData?.settings || !currency
        || (!settingIsWizard && settingsData.settings.transferMode !== TransferMode.Legacy));
    const defaults: TransferWizardDefaults | undefined = settingsData?.settings
        ? {
              priceOption: settingsData.settings.transferDefaultPriceOption,
              oldBookingOption: settingsData.settings.transferDefaultOldBookingOption,
          }
        : undefined;

    return {wizardMode: !unavailable && featureEnabled && settingIsWizard, loading: false, unavailable, currency, defaults};
};

export default useTransferWizardMode;
